import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { freeze, unFreeze, countItems, iconPath, moneyConverter, isFixedPaypal } from '@ui/helpers'
import Variants from './Variants'
import { globalStyle } from './cart-modal-style'
import {
  useResize,
  SyncedProp,
  useNestedSyncedState,
  useMounted,
  usePaygates,
  useSettingDefault
} from '@ui/hooks'
import { logger } from '@ui/analytics'
import PaypalButton from '@ui/components/shared/PaypalButton'
import { useGlobalState, useLocalOrder, isPayingWithPaypalKey } from '@libs/client'
import { useGtag } from '@ui/hooks/useGtag'
import getConfig from 'next/config'
interface ICartModal {
  productTitle?: string
  activeSyncedProp: SyncedProp<boolean>
  onCheckout: () => void
  isCheckingOutSyncedProp: SyncedProp<boolean>
}

const CartModal: FC<ICartModal> = ({
  productTitle,
  activeSyncedProp,
  onCheckout,
  isCheckingOutSyncedProp
}) => {
  const paygates = usePaygates()
  const paypalEx = paygates?.paypalex
  const [localOrder] = useLocalOrder()
  const variantItems = localOrder?.items ?? []
  const [isPayingWithPaypal] = useGlobalState<boolean>(isPayingWithPaypalKey)

  const { gtagTrackViewCart } = useGtag()

  const [syncedActive, setSyncedActive] = activeSyncedProp
  const [isCheckingOut] = useNestedSyncedState(isCheckingOutSyncedProp)
  const [maxHeight, setMaxHeight] = useState(0)
  const [totalItems, setTotalItems] = useState<number>(countItems(variantItems))
  const cartActive = getIsCartActive()
  const [isPurchase, setIsPuchase] = useState(true)

  const preview_domain = getConfig().publicRuntimeConfig.previewdomains
  const isPayoutExpress = useMemo(() => !!paypalEx, [paypalEx])
  const settingDefault = useSettingDefault()

  useResize((event) => {
    if (event.target) {
      calculateMaxHeight(event.target)
    }
  }, 0)

  useEffect(() => {
    calculateMaxHeight(window)
  }, [])

  useEffect(() => {
    const onselless_sellable = (
      settingDefault?.setting?.settings.general?.onselless_sellable ?? ''
    ).split(',')
    if (
      window.location.hostname.endsWith(preview_domain) &&
      !onselless_sellable.includes(window.location.hostname)
    ) {
      setIsPuchase(false)
    }
    const total = countItems(variantItems)
    setTotalItems(total)
    if (!total) {
      setSyncedActive(false)
    }
  }, [variantItems])

  function getIsCartActive() {
    return variantItems.length && !syncedActive
  }

  function toggleModal() {
    setSyncedActive(!syncedActive)
  }

  function openModal() {
    toggleModal()
  }

  function calculateMaxHeight(element: any) {
    const height = element.innerHeight
    const width = element.innerWidth
    if (width > 768) {
      setMaxHeight(isPayoutExpress ? height - 356 : height - 259)
    } else {
      setMaxHeight(isPayoutExpress ? height - 310 : height - 213)
    }
  }

  const scrollRef = useRef(null)
  const scrollToBottom = () => {
    if (scrollRef.current) {
      setTimeout(() => {
        ;(scrollRef.current as any).scrollTo({
          top: (scrollRef.current as any).scrollHeight,
          behavior: 'smooth'
        })
      }, 300)
    }
  }

  useEffect(() => {
    if (syncedActive) {
      scrollToBottom()
      freeze()
      gtagTrackViewCart(localOrder?.items, localOrder?.items_amount)
      logger.logProductEvent('open_cart')
    } else {
      unFreeze()
      logger.logCart('close')
    }
  }, [syncedActive])

  const isMounted = useMounted()

  return (
    <>
      <style jsx>{globalStyle}</style>
      {isMounted && (
        <div className={`cart ${isPayingWithPaypal ? 'is-hidden' : ''}`}>
          <div className={`cart-button${cartActive ? '' : ' is-invisible'}`}>
            <a className="button is-large is-primary" onClick={() => openModal()}>
              <img src={iconPath('cart.svg')} alt="Cart" />
            </a>
            <div
              className="badge has-background-danger has-text-weight-bold is-size-7"
              onClick={() => openModal()}
            >
              {totalItems}
            </div>
          </div>
          <div
            className={`modal slide-right${syncedActive ? ' is-active' : ''}`}
            style={{
              visibility: syncedActive ? 'visible' : 'hidden'
            }}
          >
            <div className="modal-background" onClick={() => toggleModal()}></div>
            <div className="modal-card animated faster">
              <header className="modal-card-head">
                <p className="modal-card-title">Your shopping cart</p>
                <button className="delete" aria-label="close" onClick={() => toggleModal()} />
              </header>
              {/* <section className={`modal-card-body ${isUpdatingCart ? 'disabled' : ''}`}> */}
              <section className="modal-card-body">
                <div className="scrollable-content" style={{ maxHeight }} ref={scrollRef}>
                  <Variants title={productTitle} />
                </div>
                <div className="fixed-bottom">
                  <hr className="mt-0" />
                  <div className="total">
                    Subtotal
                    <span className="price">${moneyConverter(localOrder?.items_amount ?? 0)}</span>
                  </div>
                  <button
                    className={`button button--cta is-fullwidth is-primary${
                      isCheckingOut ? ' is-loading' : ''
                    }`}
                    onClick={() => onCheckout()}
                  >
                    PROCEED TO SECURED CHECKOUT
                  </button>
                  {isFixedPaypal() ? (
                    <div className={`${!paypalEx && 'd-none'}`}>
                      <div className="is-divider mt-5 mb-5" data-content="or quick checkout with" />
                      {/* TODO: Implement paypal component */}
                      <PaypalButton
                        paypalEx={paypalEx as any}
                        isBuyNow={false}
                        isPurchase={isPurchase}
                      />
                    </div>
                  ) : paypalEx ? (
                    <>
                      <div className="is-divider mt-5 mb-5" data-content="or quick checkout with" />
                      {/* TODO: Implement paypal component */}
                      <PaypalButton paypalEx={paypalEx} isBuyNow={false} isPurchase={isPurchase} />
                    </>
                  ) : null}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CartModal
